import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldMeta, JsonShape, ShapeField } from '../meta/types';
import { Button, IconButton } from '../design/primitives/Button';
import { Field, Input } from '../design/primitives/Input';
import { blankFor } from './types';
import type { FieldEditorProps, Widgets } from './registry';
import { FieldEditor, renderField } from './registry';

// JSON shapes: the editors and views behind `FieldMeta.shape`. An `object` shape is a nested
// sub-form (each child is an ordinary FieldEditor), a `list` is add/remove rows of the item
// editor, a `map` is key → value rows, and `custom` defers to a registered widget. Shapes
// nest, so a list of objects or a map of maps renders the same way at every depth.

/** Promote a ShapeField to the FieldMeta the editors and renderers expect. */
export function shapeFieldMeta(sf: ShapeField, parent?: FieldMeta): FieldMeta {
  return {
    name: sf.name,
    label: sf.label,
    type: sf.type,
    required: Boolean(sf.required),
    readOnly: false,
    nullable: !sf.required,
    filterable: false,
    sortable: false,
    searchable: false,
    inCreate: true,
    inUpdate: true,
    inList: false,
    inDetail: true,
    ...(sf.enum ? { enum: sf.enum } : {}),
    ...(sf.help ? { help: sf.help } : {}),
    ...(sf.default !== undefined ? { default: sf.default } : {}),
    ...(sf.shape ? { shape: sf.shape } : {}),
    ...(sf.relation ? { relation: sf.relation } : {}),
    ...(parent?.currencyField ? { currencyField: parent.currencyField } : {}),
  };
}

type Obj = Record<string, unknown>;
const asObj = (v: unknown): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {});
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

export function ShapeEditor(p: FieldEditorProps & { shape: JsonShape; widgets?: Widgets }) {
  const { shape } = p;
  if (shape.kind === 'custom') {
    const W = p.widgets?.[shape.widget];
    if (W) return <W mode="edit" {...p} />;
    return <div className="hf-muted">Widget “{shape.widget}” is not registered.</div>;
  }
  if (shape.kind === 'object') return <ObjectEditor {...p} shape={shape} />;
  if (shape.kind === 'list') return <ListEditor {...p} shape={shape} />;
  return <MapEditor {...p} shape={shape} />;
}

function ObjectEditor(p: FieldEditorProps & { shape: Extract<JsonShape, { kind: 'object' }>; widgets?: Widgets }) {
  const value = asObj(p.value);
  return (
    <div className="hf-shape hf-shape-object" role="group" aria-label={p['aria-label'] ?? p.field.label}>
      {p.shape.fields.map((sf) => {
        const f = shapeFieldMeta(sf, p.field);
        const id = `${p.id ?? p.field.name}.${sf.name}`;
        return (
          <Field key={sf.name} label={f.label} help={f.help} required={f.required} disabled={p.disabled}>
            <FieldEditor
              id={id}
              field={f}
              value={value[sf.name] ?? blankFor(f)}
              onChange={(v) => p.onChange({ ...value, [sf.name]: v })}
              row={p.row}
              disabled={p.disabled}
              widgets={p.widgets}
              aria-label={f.label}
            />
          </Field>
        );
      })}
    </div>
  );
}

function ListEditor(p: FieldEditorProps & { shape: Extract<JsonShape, { kind: 'list' }>; widgets?: Widgets }) {
  const items = asArr(p.value);
  const item = useMemo(() => shapeFieldMeta(p.shape.item, p.field), [p.shape.item, p.field]);
  // Latest value by ref so two quick "add" clicks in one tick both land (no stale closure).
  const latest = useRef(items);
  useEffect(() => {
    latest.current = items;
  }, [items]);
  const set = (next: unknown[]) => {
    latest.current = next;
    p.onChange(next);
  };
  const atMax = p.shape.max != null && items.length >= p.shape.max;
  return (
    <div className="hf-shape hf-shape-list" role="group" aria-label={p['aria-label'] ?? p.field.label}>
      {items.length === 0 && <div className="hf-muted">None yet.</div>}
      {items.map((it, i) => (
        <div key={i} className="hf-shape-row">
          <div className="hf-grow">
            <FieldEditor id={`${p.id ?? p.field.name}.${i}`} field={item} value={it} onChange={(v) => set(items.map((x, j) => (j === i ? v : x)))} row={p.row} disabled={p.disabled} widgets={p.widgets} aria-label={`${item.label} ${i + 1}`} />
          </div>
          <IconButton icon="x" label={`Remove ${item.label.toLowerCase()} ${i + 1}`} size="sm" variant="ghost" disabled={p.disabled} onClick={() => set(items.filter((_, j) => j !== i))} />
        </div>
      ))}
      <Button size="sm" variant="ghost" icon="plus" disabled={p.disabled || atMax} onClick={() => set([...latest.current, blankFor(item) ?? null])}>
        {p.shape.addLabel ?? `Add ${item.label.toLowerCase()}`}
      </Button>
    </div>
  );
}

function MapEditor(p: FieldEditorProps & { shape: Extract<JsonShape, { kind: 'map' }>; widgets?: Widgets }) {
  const value = asObj(p.value);
  const entries = Object.entries(value);
  const valueField = useMemo(() => shapeFieldMeta(p.shape.value ?? { name: 'value', label: p.shape.valueLabel ?? 'Value', type: 'text' }, p.field), [p.shape.value, p.shape.valueLabel, p.field]);
  const [newKey, setNewKey] = useState('');
  const rename = (from: string, to: string) => {
    const next: Obj = {};
    for (const [k, v] of entries) next[k === from ? to : k] = v;
    p.onChange(next);
  };
  return (
    <div className="hf-shape hf-shape-map" role="group" aria-label={p['aria-label'] ?? p.field.label}>
      {entries.length === 0 && <div className="hf-muted">No entries.</div>}
      {entries.map(([k, v]) => (
        <div key={k} className="hf-shape-row">
          <Input size="sm" value={k} aria-label={`${p.shape.keyLabel ?? 'Key'}: ${k}`} disabled={p.disabled} onChange={(e) => rename(k, e.target.value)} onBlur={(e) => { if (!e.target.value.trim()) p.onChange(Object.fromEntries(entries.filter(([kk]) => kk !== k))); }} style={{ width: 180 }} />
          <div className="hf-grow">
            <FieldEditor id={`${p.id ?? p.field.name}.${k}`} field={valueField} value={v} onChange={(nv) => p.onChange({ ...value, [k]: nv })} row={p.row} disabled={p.disabled} widgets={p.widgets} aria-label={`${valueField.label} for ${k}`} />
          </div>
          <IconButton icon="x" label={`Remove ${k}`} size="sm" variant="ghost" disabled={p.disabled} onClick={() => p.onChange(Object.fromEntries(entries.filter(([kk]) => kk !== k)))} />
        </div>
      ))}
      <div className="hf-shape-row">
        <Input size="sm" value={newKey} placeholder={p.shape.keyLabel ?? 'Key'} aria-label={`New ${(p.shape.keyLabel ?? 'key').toLowerCase()}`} disabled={p.disabled} onChange={(e) => setNewKey(e.target.value)} style={{ width: 180 }} />
        <Button size="sm" variant="ghost" icon="plus" disabled={p.disabled || !newKey.trim() || newKey.trim() in value} onClick={() => { p.onChange({ ...value, [newKey.trim()]: blankFor(valueField) ?? null }); setNewKey(''); }}>
          Add
        </Button>
      </div>
    </div>
  );
}

/** Read-only rendering of a shaped value (detail pages, list cells in compact form). */
export function ShapeView({ field, value, shape, compact, widgets }: { field: FieldMeta; value: unknown; shape: JsonShape; compact?: boolean; widgets?: Widgets }) {
  if (value == null) return <span className="hf-muted">—</span>;
  if (shape.kind === 'custom') {
    const W = widgets?.[shape.widget];
    return W ? <W mode="render" field={field} value={value} compact={compact} /> : <span className="hf-mono">{JSON.stringify(value)}</span>;
  }
  if (shape.kind === 'list') {
    const items = asArr(value);
    if (compact) return <span>{items.length} {items.length === 1 ? 'item' : 'items'}</span>;
    const item = shapeFieldMeta(shape.item, field);
    return (
      <ol className="hf-shape-view hf-shape-view-list">
        {items.length === 0 && <li className="hf-muted">None</li>}
        {items.map((it, i) => <li key={i}>{item.shape ? <ShapeView field={item} value={it} shape={item.shape} widgets={widgets} /> : renderField(item, it, undefined, { widgets })}</li>)}
      </ol>
    );
  }
  const obj = asObj(value);
  const rows: { label: string; f: FieldMeta; v: unknown }[] =
    shape.kind === 'object'
      ? shape.fields.map((sf) => ({ label: sf.label, f: shapeFieldMeta(sf, field), v: obj[sf.name] }))
      : Object.entries(obj).map(([k, v]) => ({ label: k, f: shapeFieldMeta(shape.value ?? { name: k, label: k, type: 'text' }, field), v }));
  if (compact) return <span>{rows.filter((r) => r.v != null && r.v !== '' && r.v !== false).map((r) => r.label).join(', ') || '—'}</span>;
  return (
    <dl className="hf-shape-view hf-shape-view-object">
      {rows.map((r) => (
        <div key={r.label} className="hf-shape-view-row">
          <dt className="hf-muted">{r.label}</dt>
          <dd>{r.f.shape ? <ShapeView field={r.f} value={r.v} shape={r.f.shape} widgets={widgets} /> : renderField(r.f, r.v, undefined, { widgets })}</dd>
        </div>
      ))}
    </dl>
  );
}
